import React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { getDomain } from 'tldts';

import { Messages } from '~src/codegen/Localize';
import * as Dialogs from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { ServerUrls } from '~src/commonInfra/url/ServerUrls';
import { sendUsageEvent } from '~src/spacesPlatform/hooks/useUsage';

import { LeavingContentBody } from './Leaving';

export type Classification = 'INTERNAL' | 'EXTERNAL' | 'BLOCKED';

export type UserGeneratedLink = {
  href: string;
  classification: Classification;
  anchor?: HTMLAnchorElement;
}

export const MAX_URL_LENGTH = 2048;
export const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

export const ORIGINAL_HREF_ATTRIBUTE = 'tb-original-href';
export const CLASSIFICATION_ATTRIBUTE = 'tb-link-classification';

export const REWRITTEN_ANCHOR_SELECTOR = `a[${ORIGINAL_HREF_ATTRIBUTE}][${CLASSIFICATION_ATTRIBUTE}="${asClassification('EXTERNAL')}"]`;

export const EXTERNAL_LINK_SAFETY_FEATURE_NAME = 'ExternalLinkSafety';

/**
 * React hook that scans all `<a href>` elements inside a container and applies
 * safety policies for external links.
 *
 * Features:
 * - Classifies links as INTERNAL, EXTERNAL, or BLOCKED.
 * - Rewrites external links to an interstitial (e.g. `/#/leave?to=<url>`).
 * - Disables blocked links so they cannot be clicked.
 * - Ensures all `_blank` targets include safe `rel` attributes (to mitigate tab-nabbing).
 *
 * @example
 * ```tsx
 * function Article({ html }: { html: string }) {
 *   const { ref } = useSafeExternalLinks(html);
 *   return <div ref={ref} dangerouslySetInnerHTML={{ __html: html }} />;
 * }
 * ```
 *
 * @returns {{ ref: React.RefObject<HTMLDivElement> }}
 *   A `ref` to attach to the container whose anchor tags should be processed.
 *
 * @remarks
 * - Call this hook once per container of untrusted/UGC HTML.
 * - The hook mutates the DOM in place: links may have their `href`, `rel`,
 *   and `target` attributes updated, or be disabled entirely.
 * - Inner HTML can change after mount (e.g. update description), pass the 
 *   final HTML string so anchors are re-scanned.
 *
 * @param htmlContent - When this changes, anchors in the container are processed again.
 */
export function useSafeExternalLinks(htmlContent?: string) {
  const ref = useRef<HTMLDivElement>(null);

  const handleClick = useCallback((e: PointerEvent) => {
    const target = e.target as Element | null;
    const anchor = target?.closest<HTMLAnchorElement>(REWRITTEN_ANCHOR_SELECTOR);

    if (!anchor) return;

    e.preventDefault();

    const pendingLink: UserGeneratedLink = {
      href: anchor.getAttribute(ORIGINAL_HREF_ATTRIBUTE),
      classification: 'EXTERNAL',
      anchor,
    };

    const usageParameters = createExtraUsageParameters(pendingLink.href);

    Dialogs.openReactConfirmationDialog<UserGeneratedLink, void>({
      action: link => {
        window.open(link.href, '_blank', 'noopener,noreferrer');

        sendUsageEvent(EXTERNAL_LINK_SAFETY_FEATURE_NAME, 'click_through', usageParameters);

        return Promise.resolve();
      },

      title: Messages.externalLinkDialogWarningTitle(),
      bodyComponent: createBodyComponent(pendingLink),
      message: Messages.externalLinkDialogWarningMessage({
        EXTERNAL_URL: L10n.elideIfLong(pendingLink.href, 50)
      }),
      actionName: Messages.externalLinkDialogOpenLinkActionLabel(),
      dialogWidth: TabDialogWidth.width375px,
      paramsForAction: pendingLink,
      actionDescribedBy: 'external-location-address'
    });

    sendUsageEvent(EXTERNAL_LINK_SAFETY_FEATURE_NAME, 'impression', usageParameters);
  }, []);

  // Rewrite external links to /leaving
  useEffect(() => {
    if (!FeatureFlags.externalLinkSafetyEnabled) return;

    const el = ref.current;
    if (!el) return;

    const anchors = el.querySelectorAll<HTMLAnchorElement>('a[href]');

    anchors.forEach((a: HTMLAnchorElement) => {
      const classification = builtInClassifier(a.href);

      a.setAttribute(CLASSIFICATION_ATTRIBUTE, classification);

      switch(classification) {
        case 'BLOCKED':
          disableLink(a);
          break;
        case 'EXTERNAL': {
          rewriteLink(a);
          break;
        }
      }

      ensureBlankIsSafe(a);
    });

    el.addEventListener('click', handleClick);

    return () => {
      el.removeEventListener('click', handleClick);
    };
  }, [handleClick, htmlContent]);

  return { ref };
}

export function createExtraUsageParameters(externalHref: string) {
  return {
    a: externalHref,
    b: getDomain(externalHref, { allowPrivateDomains: true }),
  };
}

function asClassification(value: Classification) {
  return value;
}

function createBodyComponent(link: UserGeneratedLink) {
  return (_: Dialogs.ReactConfirmationDialogBodyProps) => {
    return <LeavingContentBody externalHref={link.href} />;
  };
}

/**
 * The purpose of this method is prevent reverse tabnapping.
 * See: https://owasp.org/www-community/attacks/Reverse_Tabnabbing
 */
function ensureBlankIsSafe(a: HTMLAnchorElement) {
  if (a.target !== '_blank') return;

  a.rel = 'noopener noreferrer';
}

function storeOriginalHref(a: HTMLAnchorElement) {
  a.setAttribute(ORIGINAL_HREF_ATTRIBUTE, a.href);
}

function disableLink(a: HTMLAnchorElement) {
  storeOriginalHref(a);
  a.removeAttribute('href');
  a.setAttribute('aria-disabled', 'true');
  a.setAttribute('tabindex', '-1');
  a.style.pointerEvents = 'none';
}

function rewriteLink(a: HTMLAnchorElement) {
  storeOriginalHref(a);
  const encoded = encodeURIComponent(a.href);

  const leavingBaseHref = ServerUrls.getInstance().leavingUrl();
  const newHref = `${leavingBaseHref}?to=${encoded}`;

  a.href = newHref;
  a.target = '_blank';
}

function builtInClassifier(href: string): Classification {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return 'BLOCKED';
  }

  if (!ALLOWED_PROTOCOLS.has(url.protocol)) {
    return 'BLOCKED';
  }

  const linkReg = getDomain(url.hostname, { allowPrivateDomains: true });
  const hereReg = getDomain(window.location.hostname, { allowPrivateDomains: true });

  // tldts.getDomain('localhost'); // returns null
  // tldts.getDomain('https://192.168.0.0'); // returns null
  //
  // ... exact match on hostname works for these cases
  if (!linkReg || !hereReg) {
    return url.hostname === window.location.hostname ? 'INTERNAL' : 'EXTERNAL';
  }

  if (linkReg === hereReg) {
    return 'INTERNAL';
  }

  return 'EXTERNAL';
}